import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PhoneCall, Activity } from "lucide-react";
import { V } from "@/lib/vocab";

/* Voice capacity monitor — super-admin console (Communities page).
   Live concurrent-call gauge vs the single-instance soft ceiling, daily/7-day peaks and
   minutes. The early-warning system: sustained peaks past ~15 → time to plan the SIP offload. */

const STATUS_STYLES = {
  ok: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Healthy", bar: "bg-emerald-500" },
  watch: { chip: "bg-amber-50 text-amber-800 border-amber-200", label: "Watch", bar: "bg-amber-500" },
  high: { chip: "bg-red-50 text-red-700 border-red-200", label: "Near ceiling", bar: "bg-redline-500" },
};

const fmtDur = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

export const VoiceCapacityCard = ({ onSummary }) => {
  const [cap, setCap] = useState(null);
  const summary = useRef(onSummary);
  summary.current = onSummary;

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/voice/capacity").then((r) => {
      if (!alive) return;
      setCap(r.data);
      summary.current?.({ status: r.data.status, label: (STATUS_STYLES[r.data.status] || STATUS_STYLES.ok).label, line: `${r.data.concurrent_now} live now` });
    }).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  if (!cap) return null;
  const st = STATUS_STYLES[cap.status] || STATUS_STYLES.ok;
  const pct = Math.min(100, Math.round((Math.max(cap.concurrent_now, cap.peak_today) / cap.soft_ceiling) * 100));

  return (
    <div data-testid="voice-capacity-card" className="bg-white rounded-2xl border border-sand-200 px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${cap.concurrent_now > 0 ? "bg-emerald-50" : "bg-sand-100"}`}>
            <PhoneCall className={`h-4.5 w-4.5 ${cap.concurrent_now > 0 ? "text-emerald-600" : "text-ink/40"}`} strokeWidth={1.75} />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Platform voice capacity</span>
              <span data-testid="vc-status" className={`text-[10px] font-bold border rounded-full px-2 py-px ${st.chip}`}>{st.label}</span>
            </div>
            <div className="text-[10px] text-muted-foreground">All {V.lcs("org")} · shared voice relay (not per number)</div>
            <div className="text-sm mt-0.5">
              <span data-testid="vc-live-now" className="font-heading font-extrabold text-lg text-ink">{cap.concurrent_now}</span>
              <span className="text-ink-500"> live now · ceiling ~{cap.soft_ceiling} concurrent</span>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-[160px] max-w-xs">
          <div className="h-1.5 rounded-full bg-sand-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${st.bar}`} style={{ width: `${Math.max(pct, cap.concurrent_now > 0 ? 4 : 0)}%` }} />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">Today&apos;s peak: <span data-testid="vc-peak-today" className="font-semibold text-ink-600">{cap.peak_today}</span> · 7-day peak: <span data-testid="vc-peak-7d" className="font-semibold text-ink-600">{cap.peak_7d}</span></div>
        </div>
        <div className="flex items-center gap-5 text-sm">
          <div>
            <div className="text-[11px] text-muted-foreground">Calls today</div>
            <div data-testid="vc-calls-today" className="font-heading font-extrabold text-ink">{cap.calls_today}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">Minutes · 7d</div>
            <div data-testid="vc-minutes-7d" className="font-heading font-extrabold text-ink">{cap.minutes_7d}</div>
          </div>
          {cap.top_communities_7d?.[0] && (
            <div className="hidden lg:block">
              <div className="text-[11px] text-muted-foreground flex items-center gap-1" title="The community with the most call minutes in the last 7 days — who is using the shared voice capacity"><Activity className="h-3 w-3" /> Heaviest caller · 7d</div>
              <div data-testid="vc-busiest" className="text-xs font-semibold text-ink-600 truncate max-w-[260px]" title={`${cap.top_communities_7d[0].name}: ${cap.top_communities_7d[0].minutes} of ${cap.minutes_7d} call minutes this week`}>
                {cap.top_communities_7d[0].name} <span className="text-muted-foreground font-medium">— {cap.top_communities_7d[0].minutes} of {cap.minutes_7d} min</span>
              </div>
            </div>
          )}
        </div>
      </div>
      {cap.live_calls?.length > 0 && (
        <div data-testid="vc-live-list" className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-sand-100">
          {cap.live_calls.slice(0, 6).map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {c.tenant_name || c.tenant_id} · {fmtDur(c.seconds)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
